// Blog schemas — motiflink §5, M5. Design note: docs/design-notes/m5-blog.md.
//
// Shared by the API (Zod in/out), the admin editor, and the marketing SSR
// routes. The slugify + YouTube-id helpers live HERE rather than in any one app
// so admin's preview and Astro's renderer cannot disagree about what a given
// youtube_url resolves to — that divergence is exactly how the preview/`type`
// vs `blockType` bug happened in M4.
//
// ⚠️ POSTS ARE SINGLE-LANGUAGE (owner ruling, 2026-08-15). A post is written in
// Arabic OR English — never both. `language_code` decides which listing it
// appears in, and there is NO per-post translation overlay: motiflink §5.1's
// dual mechanism was dropped because it made the editor ambiguous (setting the
// language select and adding a translation looked like the same action).
//
// So: no `translations` on posts, and no `translations` on categories either —
// a category carries ONE shared name used by both locales. If you find yourself
// re-adding an overlay here, that is a product decision to reopen with the
// owner, not a gap to fill.

import { z } from 'zod';

export const blogLocale = z.enum(['ar', 'en']);
export type BlogLocale = z.infer<typeof blogLocale>;

// ─── Slugs — Arabic-aware ───────────────────────────────────────────────────
//
// Arabic-script slugs are allowed (motiflink §5.1), so `/blog/الحروف-العربية`
// is a valid URL. ⚠️ Every frontend fetch MUST encodeURIComponent(slug) — a raw
// Arabic slug in a fetch URL is a mangled request.

export const blogSlug = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[\p{Script=Arabic}a-z0-9-]+$/u, 'lowercase latin, Arabic script, digits and hyphens');

// Arabic combining marks: harakat/tanween (U+064B–U+0652), superscript alef
// (U+0670), and tatweel (U+0640) which is pure decoration and must not survive
// into a URL.
const ARABIC_DIACRITICS = /[ً-ْٰـ]/g;

/**
 * Title → slug. Lowercases, strips Arabic diacritics + tatweel, drops anything
 * that is not Arabic/latin/digit/space/hyphen, collapses whitespace to hyphens.
 *
 * Used by admin for NEW posts only — auto-slugifying an existing post's title
 * would silently change its URL and 404 every link already published.
 */
export function slugifyBlog(input: string): string {
  return (
    input
      .trim()
      .toLowerCase()
      .replace(ARABIC_DIACRITICS, '')
      .replace(/[^\p{Script=Arabic}a-z0-9\s-]/gu, '')
      .replace(/[\s-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 120)
      // A trailing hyphen can reappear after the 120-char cut.
      .replace(/-+$/, '')
  );
}

// ─── YouTube ────────────────────────────────────────────────────────────────

// Accepts a full watch URL, a youtu.be short link, or an /embed/ URL. Empty
// string is handled by the input schemas below (it CLEARS the field to null).
export const youtubeUrl = z
  .string()
  .trim()
  .max(500)
  .regex(/(?:youtube\.com|youtu\.be)\//, 'must be a youtube.com or youtu.be URL');

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Extract the 11-char video id from any accepted YouTube URL shape.
 * Returns null when nothing resolves — callers then fall back to the featured
 * image rather than rendering a broken embed.
 */
export function youtubeIdFrom(url: string | null | undefined): string | null {
  if (!url) return null;
  const raw = url.trim();
  if (!raw) return null;

  // Bare id pasted straight in.
  if (YOUTUBE_ID.test(raw)) return raw;

  let parsed: URL;
  try {
    parsed = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, '');
  let candidate: string | null = null;

  if (host === 'youtu.be') {
    candidate = parsed.pathname.split('/').filter(Boolean)[0] ?? null;
  } else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
    const v = parsed.searchParams.get('v');
    if (v) {
      candidate = v;
    } else {
      const parts = parsed.pathname.split('/').filter(Boolean);
      // /embed/<id>, /v/<id>, /shorts/<id>
      const idx = parts.findIndex((p) => p === 'embed' || p === 'v' || p === 'shorts');
      candidate = idx >= 0 ? (parts[idx + 1] ?? null) : null;
    }
  }

  return candidate && YOUTUBE_ID.test(candidate) ? candidate : null;
}

/** The privacy-preserving embed URL. youtube-nocookie per motiflink §5.3. */
export function youtubeEmbedUrl(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}`;
}

/** Thumbnail for the admin editor's live preview. */
export function youtubeThumbnailUrl(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

// ─── Categories ─────────────────────────────────────────────────────────────
//
// A category carries ONE shared name used by both locales (owner, 2026-08-15).
// Prefer a language-neutral name, since the same string renders on /blog and
// /en/blog alike. No translations column — see the header note.

// strictObject, not object: a stale client still sending `translations` (removed
// in 0010) must get a LOUD 400 rather than have the field silently stripped —
// a silent strip looks like a successful save that quietly lost the copy.
export const blogCategoryCreateInput = z.strictObject({
  name: z.string().trim().min(1).max(160),
  slug: blogSlug.optional(), // admin auto-slugifies from name when omitted
  iconUrl: z.string().trim().max(1000).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type BlogCategoryCreateInput = z.infer<typeof blogCategoryCreateInput>;

export const blogCategoryUpdateInput = blogCategoryCreateInput.partial();
export type BlogCategoryUpdateInput = z.infer<typeof blogCategoryUpdateInput>;

export const blogCategoryDto = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  iconUrl: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BlogCategoryDto = z.infer<typeof blogCategoryDto>;

// ─── Posts ──────────────────────────────────────────────────────────────────

// Empty string CLEARS youtube_url to null (motiflink §5.2); any other value
// must look like a YouTube URL.
const optionalYoutube = z
  .union([z.literal(''), youtubeUrl])
  .nullable()
  .optional();

// strictObject for the same reason as categories above: `translations` is gone,
// and a client that still sends it must be told, not silently obeyed.
export const blogPostCreateInput = z.strictObject({
  title: z.string().trim().min(1).max(300),
  slug: blogSlug,
  excerpt: z.string().trim().max(1000).nullable().optional(),
  content: z.string().max(200_000).nullable().optional(),
  featuredImage: z.string().trim().max(1000).nullable().optional(),
  youtubeUrl: optionalYoutube,
  categoryId: z.uuid().nullable().optional(),
  languageCode: blogLocale.optional(),
  isPublished: z.boolean().optional(),
});
export type BlogPostCreateInput = z.infer<typeof blogPostCreateInput>;

// authorId is NEVER accepted from the client — it is stamped from the session.
export const blogPostUpdateInput = blogPostCreateInput.partial();
export type BlogPostUpdateInput = z.infer<typeof blogPostUpdateInput>;

// List projection — deliberately WITHOUT `content` (motiflink §5.2). A listing
// of 12 posts must not ship 12 full article bodies.
export const blogPostListItemDto = z.object({
  id: z.uuid(),
  title: z.string(),
  slug: z.string(),
  excerpt: z.string().nullable(),
  featuredImage: z.string().nullable(),
  youtubeUrl: z.string().nullable(),
  languageCode: blogLocale,
  isPublished: z.boolean(),
  publishedAt: z.string().nullable(),
  categoryId: z.uuid().nullable(),
  categoryName: z.string().nullable(),
  categorySlug: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BlogPostListItemDto = z.infer<typeof blogPostListItemDto>;

export const blogPostDto = blogPostListItemDto.extend({
  content: z.string().nullable(),
  authorId: z.uuid().nullable(),
});
export type BlogPostDto = z.infer<typeof blogPostDto>;

export const blogListQuery = z.object({
  category: z.string().trim().max(120).optional(),
  lang: blogLocale.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(12),
  offset: z.coerce.number().int().min(0).default(0),
});
export type BlogListQuery = z.infer<typeof blogListQuery>;

// Admin listing sees drafts too, so it needs an explicit published filter.
export const blogAdminListQuery = z.object({
  category: z.string().trim().max(120).optional(),
  lang: blogLocale.optional(),
  published: z.enum(['true', 'false']).optional(),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type BlogAdminListQuery = z.infer<typeof blogAdminListQuery>;

// ─── Inline/featured image upload ───────────────────────────────────────────
//
// The upload-first endpoint returns a URL with NO DB side effect, so a brand-new
// unsaved post can already carry a featured image and TipTap can insert inline
// images mid-draft. Persisting the URL is the caller's save.

export const BLOG_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

export const BLOG_IMAGE_EXTS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

export const blogImageUploadDto = z.object({ url: z.string() });
export type BlogImageUploadDto = z.infer<typeof blogImageUploadDto>;
